import { ChatMessageAttachments, type ChatAttachmentView } from '@/components/ChatMessageAttachments';
import { ChatImageAlbum } from '@/components/ChatImageAlbum';
import { PwaInlineAudioPlayer } from '@/components/PwaInlineAudioPlayer';
import { useLongPress } from '@/hooks/useLongPress';
import { resolveWidgetMediaUrl } from '@/lib/resolveWidgetMediaUrl';
import { formatMessage } from '@/lib/formatMessage';

export type ChatMessageView = {
  id: string;
  content: string;
  sender: string;
  timestamp: string;
  agentSource?: string | null;
  isWhisper?: boolean;
  semanticShortcut?: boolean;
  attachments?: ChatAttachmentView[];
  /** Raw Whisper transcription text */
  audioTranscription?: string | null;
  /** Transcription status: 'success' | 'failed' | 'duration_exceeded' | 'download_failed' | 'disabled' */
  transcriptionStatus?: string | null;
  /** WhatsApp: pending | sent | delivered | read | failed */
  deliveryStatus?: string | null;
  operatorRating?: string | null;
  operatorFeedbackComment?: string | null;
  operatorFeedbackDesired?: string | null;
};

function bubbleClasses(m: ChatMessageView): string {
  if (m.isWhisper) return 'chat-bubble chat-bubble--whisper';
  if (m.sender === 'visitor') return 'chat-bubble chat-bubble--visitor';
  return 'chat-bubble chat-bubble--agent';
}

function alignClass(m: ChatMessageView): string {
  if (m.isWhisper) return 'chat-bubble-row chat-bubble-row--center';
  return m.sender === 'visitor' ? 'chat-bubble-row chat-bubble-row--left' : 'chat-bubble-row chat-bubble-row--right';
}

function albumAlignClass(sender: string): string {
  return sender === 'visitor' ? 'chat-bubble-row chat-bubble-row--left' : 'chat-bubble-row chat-bubble-row--right';
}

type Props = {
  message: ChatMessageView;
  showMeta?: boolean;
  /** Canal del hilo: habilita ticks en WhatsApp aunque falte deliveryStatus histórico. */
  channel?: string | null;
  /** Mostrar 👎 al lado de la burbuja (respuestas del asistente). */
  onThumbsDown?: () => void;
  /** Mensaje fijo (handoff): 👎 visible pero no editable. */
  thumbsDownLocked?: boolean;
  /** Reenviar si WhatsApp no dio acuse (sent/failed). */
  onRetryDelivery?: () => void;
  retryingDelivery?: boolean;
  /** Tras esperar el acuse; si es false el botón queda deshabilitado. */
  retryReady?: boolean;
  /** La reserva apunta a este mensaje. */
  highlighted?: boolean;
  /** Mantener pulsado: borrar para todos. */
  onLongPress?: () => void;
};

const TRANSCRIPTION_STATUS_LABELS: Record<string, string> = {
  failed: 'Transcripción fallida',
  disabled: 'Transcripción deshabilitada',
  duration_exceeded: 'Duración excedida',
  download_failed: 'Error de descarga',
};

/** Refs that are not real playable URLs (placeholders or channel-specific IDs). */
function isPlayableAudioUrl(rawSrc: string): boolean {
  if (!rawSrc) return false;
  // Known placeholders
  if (['demo-recording', 'widget-upload', 'testchat-recording'].includes(rawSrc)) return false;
  // Must be a URL path or full URL to be playable
  return rawSrc.startsWith('/') || rawSrc.startsWith('http://') || rawSrc.startsWith('https://') || rawSrc.startsWith('data:');
}

export function ChatAlbumRow({
  sender,
  images,
  onLongPress,
}: {
  sender: string;
  images: ChatAttachmentView[];
  onLongPress?: () => void;
}) {
  const hold = useLongPress(onLongPress);
  return (
    <div
      className={[albumAlignClass(sender), onLongPress ? 'chat-bubble-row--holdable' : ''].filter(Boolean).join(' ')}
      {...hold}
    >
      <div className="chat-image-album-wrap">
        <ChatImageAlbum images={images} />
      </div>
    </div>
  );
}

function DoubleCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 11" width="16" height="11" aria-hidden>
      <path
        fill="currentColor"
        d="M11.151.323l-6.243 6.43-2.374-2.266-.908.94 3.316 3.164 7.148-7.36-.939-.908zm3.837 0l-6.243 6.43-1.077-1.028-.908.94 2.019 1.926 7.148-7.36-.939-.908z"
      />
    </svg>
  );
}

function SingleCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 12 11" width="12" height="11" aria-hidden>
      <path
        fill="currentColor"
        d="M11.151.323l-6.243 6.43-2.374-2.266-.908.94 3.316 3.164 7.148-7.36z"
      />
    </svg>
  );
}

function deliveryTicks(
  status: string | null | undefined
): { label: string; className: string; kind: 'pending' | 'single' | 'double' | 'failed' } | null {
  if (!status) return null;
  switch (status) {
    case 'pending':
      return { label: 'Enviando', className: 'chat-bubble__ticks chat-bubble__ticks--pending', kind: 'pending' };
    case 'sent':
      return { label: 'Enviado', className: 'chat-bubble__ticks chat-bubble__ticks--sent', kind: 'single' };
    case 'delivered':
      return { label: 'Recibido', className: 'chat-bubble__ticks chat-bubble__ticks--delivered', kind: 'double' };
    case 'read':
      return { label: 'Leído', className: 'chat-bubble__ticks chat-bubble__ticks--read', kind: 'double' };
    case 'failed':
      return { label: 'Error de envío', className: 'chat-bubble__ticks chat-bubble__ticks--failed', kind: 'failed' };
    default:
      return null;
  }
}

function resolveDeliveryStatus(m: ChatMessageView, channel?: string | null): string | null | undefined {
  if (m.deliveryStatus) return m.deliveryStatus;
  // Mensajes agent en WhatsApp anteriores al tracking: mostrar al menos "enviado"
  if (
    !m.isWhisper &&
    m.sender !== 'visitor' &&
    (channel === 'whatsapp_meta' || channel === 'whatsapp_js')
  ) {
    return 'sent';
  }
  return m.deliveryStatus;
}

export function ChatMessageBubble({
  message: m,
  showMeta = true,
  channel,
  onThumbsDown,
  thumbsDownLocked = false,
  onRetryDelivery,
  retryingDelivery = false,
  retryReady = true,
  highlighted = false,
  onLongPress,
}: Props) {
  const hold = useLongPress(onLongPress);
  const time = new Date(m.timestamp).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });

  const audioAttachment = m.attachments?.find((a) => a.kind === 'audio');
  const nonAudioAttachments = m.attachments?.filter((a) => a.kind !== 'audio');
  const isAudioMessage = !!audioAttachment;
  const transcriptionFailed = m.transcriptionStatus && m.transcriptionStatus !== 'success';
  // Don't show content text if it's the same as the transcription (avoids duplication)
  const showContentText = m.content && m.content !== '[Archivos adjuntos]' && m.content !== '[Audio]'
    && !(isAudioMessage && m.audioTranscription && m.content === m.audioTranscription);
  const showTicks = !m.isWhisper && m.sender !== 'visitor';
  const resolvedStatus = resolveDeliveryStatus(m, channel);
  const ticks = showTicks ? deliveryTicks(resolvedStatus) : null;
  const retryableStatus =
    m.deliveryStatus === 'pending' || m.deliveryStatus === 'sent' || m.deliveryStatus === 'failed';
  const canTapRetry = Boolean(onRetryDelivery) && retryableStatus;
  // Los mensajes de audio no usan atajo semántico (ni el prefijo *).
  const showSemanticMarker = Boolean(m.semanticShortcut && !m.isWhisper && !isAudioMessage);
  const showThumbsDown = Boolean(onThumbsDown) && !m.isWhisper && m.sender === 'agent';
  const thumbsActive = m.operatorRating === 'bad';

  return (
    <div
      id={`mensaje-${m.id}`}
      className={[alignClass(m), highlighted ? 'chat-bubble--linked' : '', onLongPress ? 'chat-bubble-row--holdable' : '']
        .filter(Boolean)
        .join(' ')}
      {...hold}
    >
      {showThumbsDown ?
        <button
          type="button"
          className={[
            'chat-bubble__thumbs-down',
            thumbsActive ? 'chat-bubble__thumbs-down--active' : '',
            thumbsDownLocked ? 'chat-bubble__thumbs-down--locked' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          title={
            thumbsDownLocked
              ? 'Este mensaje es fijo (handoff) y no se puede cambiar'
              : 'Mejorar respuesta'
          }
          aria-label={
            thumbsDownLocked
              ? 'Mensaje fijo de handoff; no se puede mejorar'
              : 'Mejorar respuesta'
          }
          aria-disabled={thumbsDownLocked}
          aria-pressed={thumbsActive}
          onClick={(e) => {
            e.stopPropagation();
            onThumbsDown?.();
          }}
        >
          👎
        </button>
      : null}
      <div className={bubbleClasses(m)}>
        <div className="chat-bubble__header">
          <span className="chat-bubble__sender">
            {m.isWhisper ? 'Susurro (solo IA)' : m.sender}
            {!m.isWhisper && m.agentSource === 'human' ? ' · operador' : ''}
            {!m.isWhisper && m.agentSource === 'handoff' ? ' · handoff' : ''}
          </span>
        </div>
        {showSemanticMarker ?
          <div className="chat-bubble__text">
            * {formatMessage(m.content)}
          </div>
        : showContentText ?
          <div className="chat-bubble__text">{formatMessage(m.content)}</div>
        : null}

        {isAudioMessage ? (
          <div className="chat-bubble__audio-section">
            {(() => {
              const rawSrc = audioAttachment.url || audioAttachment.ref || '';
              if (!isPlayableAudioUrl(rawSrc)) {
                return (
                  <div className="chat-bubble__media-audio-fallback" aria-label="Mensaje de audio">
                    🎤 Audio
                  </div>
                );
              }
              const src = resolveWidgetMediaUrl(rawSrc);
              return (
                <PwaInlineAudioPlayer
                  src={src}
                  durationSeconds={audioAttachment.durationSeconds}
                />
              );
            })()}

            {m.audioTranscription ? (
              <div className="chat-bubble__audio-transcription">
                {m.audioTranscription}
              </div>
            ) : null}

            {transcriptionFailed ? (
              <div className="chat-bubble__audio-status">
                {TRANSCRIPTION_STATUS_LABELS[m.transcriptionStatus!] ?? 'Error de transcripción'}
              </div>
            ) : null}
          </div>
        ) : null}

        {nonAudioAttachments?.length ?
          <ChatMessageAttachments attachments={nonAudioAttachments} />
        : null}

        {showMeta ?
          <div className="chat-bubble__footer">
            <time className="chat-bubble__time" dateTime={m.timestamp}>
              {time}
            </time>
            {ticks ?
              canTapRetry ?
                <button
                  type="button"
                  className={`${ticks.className} chat-bubble__ticks-btn`}
                  title={
                    retryingDelivery ? 'Reenviando…'
                    : !retryReady ? 'Esperando acuse de WhatsApp…'
                    : ticks.kind === 'failed' ? 'Error de envío. Tocá para reenviar'
                    : 'Sin acuse. Tocá para reenviar'
                  }
                  aria-label={
                    retryingDelivery ? 'Reenviando mensaje'
                    : !retryReady ? 'Esperando acuse de WhatsApp'
                    : 'Reenviar mensaje'
                  }
                  disabled={retryingDelivery || !retryReady}
                  onClick={onRetryDelivery}
                >
                  {retryingDelivery ?
                    <span className="chat-bubble__ticks-pending-mark" aria-hidden>
                      ···
                    </span>
                  : ticks.kind === 'failed' ?
                    <span className="chat-bubble__ticks-failed-mark" aria-hidden>
                      !
                    </span>
                  : ticks.kind === 'single' ?
                    <SingleCheckIcon />
                  : <DoubleCheckIcon />}
                </button>
              : <span className={ticks.className} title={ticks.label} aria-label={ticks.label}>
                  {ticks.kind === 'pending' ?
                    <span className="chat-bubble__ticks-pending-mark" aria-hidden>
                      ···
                    </span>
                  : ticks.kind === 'failed' ?
                    <span className="chat-bubble__ticks-failed-mark" aria-hidden>
                      !
                    </span>
                  : ticks.kind === 'single' ?
                    <SingleCheckIcon />
                  : <DoubleCheckIcon />}
                </span>
            : null}
          </div>
        : null}
      </div>
    </div>
  );
}

export function ChatTypingBubble() {
  return (
    <div className="chat-bubble-row chat-bubble-row--left">
      <div className="chat-bubble chat-bubble--visitor chat-bubble--typing" aria-label="Visitante escribiendo">
        <span className="chat-bubble__typing-dot" />
        <span className="chat-bubble__typing-dot" />
        <span className="chat-bubble__typing-dot" />
      </div>
    </div>
  );
}
